import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Lock, 
  LogOut, 
  Download, 
  Users, 
  MapPin, 
  Calendar,
  TrendingUp,
  AlertCircle,
  Loader2,
  Search,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'sigma_admin_token';

const Admin = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [authChecking, setAuthChecking] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setAuthChecking(false);
        return;
      }
      try {
        await axios.get(`${API}/admin/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } finally {
        setAuthChecking(false);
      }
    };
    verifyToken();
  }, [token]);

  const handleLogin = (newToken) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  if (!token) {
    return <LoginPage onLogin={handleLogin} navigate={navigate} />;
  }

  return <Dashboard token={token} onLogout={handleLogout} navigate={navigate} />;
};


const LoginPage = ({ onLogin, navigate }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success && response.data.token) {
        onLogin(response.data.token);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-none" data-testid="admin-login-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-blue-900" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">Admin Dashboard</CardTitle>
          <p className="text-gray-600 text-sm mt-2">Sigma International School</p>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2" data-testid="login-error">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-medium">Admin Password</Label>
              <Input
                id="password"
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                required
                autoFocus
                className="border-gray-300 focus:border-blue-900 focus:ring-blue-900"
              />
            </div>
            <Button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading || !password}
              className="w-full bg-blue-900 hover:bg-blue-800 text-white font-semibold py-5 text-base"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Authenticating...
                </>
              ) : (
                'Login'
              )}
            </Button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full text-sm text-gray-500 hover:text-gray-700 mt-2"
            >
              ← Back to home
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};


const Dashboard = ({ token, onLogout, navigate }) => {
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [exporting, setExporting] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [statsRes, entriesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeaders }),
        axios.get(`${API}/admin/waitlist?limit=500`, { headers: authHeaders })
      ]);
      setStats(statsRes.data);
      setEntries(entriesRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      }
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/export`, {
        headers: authHeaders,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `sigma_waitlist_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  const filteredEntries = entries.filter(entry => {
    const search = searchTerm.toLowerCase();
    return (
      entry.parent_name?.toLowerCase().includes(search) ||
      entry.email?.toLowerCase().includes(search) ||
      entry.phone?.toLowerCase().includes(search) ||
      entry.city?.toLowerCase().includes(search)
    );
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="/images/sigma-logo-1.png"
                alt="Sigma"
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Waitlist Admin</h1>
                <p className="text-xs text-gray-500">Sigma International School</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                data-testid="view-site-btn"
              >
                View Site
              </Button>
              <Button
                onClick={onLogout}
                variant="outline"
                size="sm"
                data-testid="logout-btn"
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card data-testid="stat-total" className="border-l-4 border-l-blue-900">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Total Founding Families</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total || 0}</p>
                </div>
                <Users className="w-10 h-10 text-blue-900/30" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-recent" className="border-l-4 border-l-orange-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Last 7 Days</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.recent_7_days || 0}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-orange-500/30" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-cities" className="border-l-4 border-l-emerald-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Cities Reached</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.by_city?.length || 0}</p>
                </div>
                <MapPin className="w-10 h-10 text-emerald-600/30" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Breakdown by City & Age */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card data-testid="city-breakdown">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-blue-900" />
                Top Cities
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.by_city?.length ? (
                <div className="space-y-3">
                  {stats.by_city.slice(0, 8).map((c, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-gray-700">{c.city}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-32 bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div 
                            className="bg-blue-900 h-2 rounded-full" 
                            style={{ width: `${(c.count / stats.by_city[0].count) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-semibold text-gray-900 w-8 text-right">{c.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No data yet</p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="age-breakdown">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-orange-500" />
                Child Age Groups
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.by_age?.length ? (
                <div className="space-y-3">
                  {stats.by_age.map((a, i) => {
                    const maxCount = Math.max(...stats.by_age.map(x => x.count));
                    return (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-gray-700">{a.age_group} years</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div 
                              className="bg-orange-500 h-2 rounded-full" 
                              style={{ width: `${(a.count / maxCount) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-semibold text-gray-900 w-8 text-right">{a.count}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No data yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Entries Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-lg">All Waitlist Entries</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    data-testid="search-input"
                    type="text"
                    placeholder="Search by name, email, phone, city"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  size="sm"
                  disabled={refreshing}
                  data-testid="refresh-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  onClick={handleExport}
                  disabled={exporting || entries.length === 0}
                  data-testid="export-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredEntries.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {entries.length === 0 ? 'No entries yet. Once parents start signing up, they will appear here.' : 'No entries match your search.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="entries-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Parent Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Child Age</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEntries.map((entry, i) => (
                      <TableRow key={entry.id || i} data-testid={`entry-row-${i}`}>
                        <TableCell className="text-gray-500">{i + 1}</TableCell>
                        <TableCell className="font-medium">{entry.parent_name}</TableCell>
                        <TableCell>
                          <a 
                            href={`https://wa.me/91${entry.phone}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-700 hover:underline"
                          >
                            {entry.phone}
                          </a>
                        </TableCell>
                        <TableCell>
                          <a href={`mailto:${entry.email}`} className="text-blue-700 hover:underline">
                            {entry.email}
                          </a>
                        </TableCell>
                        <TableCell>{entry.child_age}</TableCell>
                        <TableCell>{entry.city}</TableCell>
                        <TableCell className="text-sm text-gray-600">{formatDate(entry.timestamp)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {filteredEntries.length > 0 && (
              <p className="text-sm text-gray-500 mt-4">
                Showing {filteredEntries.length} of {entries.length} entries
              </p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Admin;
